

import { useCallback, useEffect, useState } from 'react';
import { API_ORIGIN, GAMI_ENABLED } from '../utils/homeHelpers';
import {
  _computeGamificationFromStats,
  _normalizeGamiForUI,
} from '../utils/gamificationHelpers';
import {
  _computeStreak,
  _computeStreakFromDays,
  _loadTriviaHistory,
} from '../utils/triviaHelpers';
import {
  _loadGamiCache,
  _saveGamiCache,
} from '../utils/userStatsCache';

const useGamification = ({ resolvedUid, userStats }) => {
  const [gami, setGami] = useState(() => _loadGamiCache(resolvedUid));

  const fetchGamification = useCallback(async () => {
    // Base stats (por si necesitamos fallback local)
    const baseStats = userStats || {};
    let recentDays = [];

    try {
      const rA = Array.isArray(baseStats.last7Days) ? baseStats.last7Days : [];
      const rB = Array.isArray(baseStats.recent) ? baseStats.recent : [];
      const r = rA.length ? rA : rB;

      recentDays = (Array.isArray(r) ? r : [])
        .filter((x) => Number(x?.score || 0) > 0)
        .map((x) => String(x?.day || ''))
        .filter(Boolean);
    } catch {}

    const derivedStreak = Math.max(
      Number(baseStats.streak || 0),
      Number(_computeStreakFromDays(recentDays) || 0),
      Number(_computeStreak(_loadTriviaHistory(resolvedUid)) || 0)
    );

    const statsForGami = {
      ...baseStats,
      streak: Number.isFinite(derivedStreak) ? derivedStreak : Number(baseStats.streak || 0),
    };

    // Si no hay UID, caer a local inmediatamente
    if (!resolvedUid) {
      const local = _normalizeGamiForUI(_computeGamificationFromStats(statsForGami), statsForGami);
      setGami(local);
      _saveGamiCache(resolvedUid, local);
      return;
    }

    // 1) Intentar SIEMPRE backend primero (ignoramos cualquier flag previo/404 cacheado)
    let backendOk = false;

    try {
      const url = `${API_ORIGIN}/api/gamification/profile?userId=${encodeURIComponent(resolvedUid)}`;
      const r = await fetch(url, { credentials: 'include', cache: 'no-store' });

      if (r.ok) {
        const j = await r.json().catch(() => null);
        if (j && j.ok) {
          const merged = _normalizeGamiForUI(j, statsForGami);
          setGami(merged);
          _saveGamiCache(resolvedUid, merged);
          backendOk = true;
        }
      }
    } catch {
      // ignora, probamos fallback
    }

    // 2) Si backend no entregó datos OK, usar cálculo local
    if (!backendOk) {
      const local = _normalizeGamiForUI(_computeGamificationFromStats(statsForGami), statsForGami);
      setGami(local);
      _saveGamiCache(resolvedUid, local);
    }
  }, [resolvedUid, userStats]);

  // Limpieza de bandera legacy: si alguna vez se guardó un "api404" viejo, lo borramos.
  useEffect(() => {
    try { localStorage.removeItem('gami:api404'); } catch {}
  }, []);

  // Debounce: only allow one fetch every 30s globally, and retry once after 1 min if failed
  useEffect(() => {
    if (!GAMI_ENABLED || !resolvedUid) return;

    const last = window.__lastGamiFetch || 0;
    const now = Date.now();
    if (now - last < 30000) return;

    window.__lastGamiFetch = now;
    fetchGamification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUid]);

  return {
    gami,
    setGami,
    fetchGamification,
  };
};

export default useGamification;